import { AssetTypeSwatch } from './asset.type.swatch';
import { addStyles, rgba } from './theme';
import { BLACK, WHITE } from './theme.palette';
import { m, type MithrilVNode } from '../mithril';

export interface CollectionAssetTypeMarkerAttrs {
	icon: string;
	color: string;
	/** Outer marker diameter in px — the inner white ring and icon are sized proportionally to it. */
	diameter: number;
	/** Degrees clockwise to rotate the glyph (e.g. Valve's hourglass, turned on its side). */
	rotate?: number;
}

/** How much bigger a collection marker gets when it has a selected asset type, so its icon stays legible. */
export const ASSET_TYPE_MARKER_SCALE = 1.5;

export function collectionMarkerRadius(baseRadius: number, hasAssetType: boolean): number {
	return hasAssetType ? baseRadius * ASSET_TYPE_MARKER_SCALE : baseRadius;
}

/** Fraction of the outer marker diameter occupied by the inner white ring — the rest is the visible
 *  collection-color collar. Kept close to 1 so that collar reads as a thin ring/border, not a thick band. */
const RING_SCALE = 0.85;
/** Fraction of the ring occupied by the icon — exported for the map print capture, which replaces the
 *  icon-font glyph with a plain background-image sized by this same ratio (@see `map.print.capture.ts`). */
export const ICON_TO_RING_SCALE = 0.85;

const CSS = {
	ring: 'c-CollectionAssetTypeMarker__ring',
};

addStyles({
	[CSS.ring]: {
		position: 'relative',
		borderRadius: '50%',
		background: rgba(WHITE),
		outline: `1px solid ${rgba(BLACK, 0.15)}`,
		outlineOffset: '-1px',
	},
});

export const ASSET_TYPE_RING_SELECTOR = `.${CSS.ring}`;
export const ASSET_TYPE_RING_DATA = {
	icon: 'data-asset-type-icon',
	color: 'data-asset-type-color',
	rotate: 'data-asset-type-rotate',
} as const;

/** The white-ring-plus-icon overlay drawn inside a collection's map marker when it has a selected asset type. */
export const CollectionAssetTypeMarker = {
	view({ icon, color, diameter, rotate }: CollectionAssetTypeMarkerAttrs): MithrilVNode {
		const ringSize = diameter * RING_SCALE;
		return m(
			'div',
			{
				className: CSS.ring,
				style: { width: `${ringSize}px`, height: `${ringSize}px` },
				[ASSET_TYPE_RING_DATA.icon]: icon,
				[ASSET_TYPE_RING_DATA.color]: color,
				[ASSET_TYPE_RING_DATA.rotate]: rotate ?? 0,
			},
			AssetTypeSwatch.view({
				icon,
				color,
				size: ringSize * ICON_TO_RING_SCALE,
				rotate,
				centerInParentSize: ringSize,
			})
		);
	},
};
